import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
    Card,
    CardBody,
    Button,
    Tooltip
} from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExport } from '@fortawesome/free-solid-svg-icons';
import OwnerTransactionForm from './OwnerTransactionForm';
import { rootUrl } from '../../../config/config';
import ReverseTransactionDialog from './ReverseTransactionDialog';
import { FaEye, FaUndoAlt, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { formatMoney } from './formatMoney';

const OwnerStatement = ({ ownerCode, ownerName }) => {
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken');

    const [ownerDisplayName, setOwnerDisplayName] = useState(ownerName || '');

    const [statement, setStatement] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        trans_type: '',
        plate_no: ''
    });
    const [showTransactionForm, setShowTransactionForm] = useState(false);
    const [currentBalance, setCurrentBalance] = useState(0);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [cars, setCars] = useState([]);
    const [reverseOpen, setReverseOpen] = useState(false);
    const [selectedTrans, setSelectedTrans] = useState(null);
    const [transTypeSummary, setTransTypeSummary] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());

    const fetchStatement = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.date_from) params.append('date_from', filters.date_from);
            if (filters.date_to) params.append('date_to', filters.date_to);
            if (filters.trans_type) params.append('trans_type', filters.trans_type);
            if (filters.plate_no) params.append('plate_no', filters.plate_no);

            const response = await axios.get(
                `${rootUrl}/api/owners/${ownerCode}/statement?${params.toString()}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );

            if (response.data && response.data.success) {
                setStatement(response.data.data || []);
                setCurrentBalance(response.data.current_balance || 0);
                setOpeningBalance(response.data.opening_balance || 0);
                setTransTypeSummary(response.data.summary || []);

                // Set owner name from API response if available
                if (response.data.owner_name) {
                    setOwnerDisplayName(response.data.owner_name);
                }
            }
        } catch (error) {
            console.error('Error fetching statement:', error);
        } finally {
            setLoading(false);
        }
    }, [ownerCode, filters, authToken]);

    const fetchCars = useCallback(async () => {
        try {
            const response = await axios.get(
                `${rootUrl}/cars/plates?owner_code=${ownerCode}`,
                { headers: { 'Authorization': `Bearer ${authToken}` } }
            );
            const payload = response.data;
            if (Array.isArray(payload)) {
                setCars(payload.map(plate => ({ plate_no: plate })));
            } else {
                console.warn('Unexpected cars payload', payload);
                setCars([]);
            }
        } catch (error) {
            console.error('Error fetching cars:', error);
            setCars([]);
        }
    }, [ownerCode, authToken]);

    useEffect(() => {
        if (ownerCode) {
            fetchStatement();
            fetchCars();
        }
    }, [ownerCode, fetchStatement, fetchCars]);

    useEffect(() => {
        // Only set from prop if we don't have a name from API yet
        if (ownerName && !ownerDisplayName) {
            setOwnerDisplayName(ownerName);
        }
    }, [ownerName, ownerDisplayName]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const clearFilters = () => {
        setFilters({
            date_from: '',
            date_to: '',
            trans_type: '',
            plate_no: ''
        });
    };

    const formatDate = (date) => {
        if (!date) return '-';
        const d = new Date(date);
        return isNaN(d.getTime())
            ? '-'
            : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const buildOwnerTransactionPath = (ownerCode, transId) => (
        ownerCode && transId ? `/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}` : null
    );

    const getDocumentNumber = (value) => (
        String(value || '').split(':').pop() || value || '-'
    );

    const isReversedDocument = (transaction) => (
        transaction?.reversed === true
        || transaction?.reversed === 1
        || transaction?.reversed === '1'
        || String(transaction?.reference_type || '').toUpperCase() === 'REVERSAL'
    );

    const getTransactionTypeLabel = (type) => {
        const types = {
            'REM': 'Remittance',
            'PAY': 'Payment',
            'RIM': 'Reimbursement',
            'CHG': 'Charge',
            'ADJ': 'Adjustment',
            'RRM': 'Reverse Remittance',
            'RPY': 'Reverse Payment',
            'RRI': 'Reverse Reimbursement',
            'RCG': 'Reverse Charge',
            'RAD': 'Reverse Adjustment',
            'REV': 'Reversal'
        };
        return types[type] || type;
    };

    const handleExport = () => {
        // TODO: Implement export functionality
        alert('Export functionality coming soon');
    };

    // refreshStatement removed (unused)

    // Determine which transactions are already reversed
    const reversedOriginalIds = new Set(
        (statement || [])
            .filter(t => t.reference_type === 'REVERSAL' && t.reference_id)
            .map(t => t.reference_id)
    );

    const reversalTypes = new Set(['REV', 'RRM', 'RPY', 'RRI', 'RCG', 'RAD', 'RJI']);

    const canReverse = (t) => {
        if (!t) return false;
        if (reversalTypes.has(String(t.trans_type || '').toUpperCase())) return false;
        if (reversedOriginalIds.has(t.id)) return false;
        return true;
    };

    const toggleRowExpand = (transId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(transId)) next.delete(transId);
            else next.add(transId);
            return next;
        });
    };

    const fmtAmt = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const openReverse = (t) => {
        setSelectedTrans(t);
        setReverseOpen(true);
    };

    const closeReverse = () => {
        setReverseOpen(false);
        setSelectedTrans(null);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="w-full max-w-7xl mx-auto px-4 py-6 pb-8">

                {/* Page Header */}
                <div className="flex items-start justify-between mb-5">
                    <div>
                        <h1 className="text-2xl font-bold text-[#2a2e30]">{ownerDisplayName || ownerName}</h1>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                            <span className="text-xs font-mono text-gray-400">{ownerCode}</span>
                            <span className="text-sm">
                                Balance: <span className={`font-bold ${currentBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatMoney(currentBalance)}</span>
                            </span>
                            {openingBalance !== 0 && (
                                <span className="text-xs text-gray-400">Opening: {formatMoney(openingBalance)}</span>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                        <Button size="sm" onClick={handleExport} className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm shadow-none text-xs px-3 py-2 normal-case flex items-center gap-1.5">
                            <FontAwesomeIcon icon={faFileExport} /> Export
                        </Button>
                        <Button size="sm" onClick={() => setShowTransactionForm(true)} className="bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm shadow-none text-xs px-3 py-2 font-semibold normal-case">
                            + Add Transaction
                        </Button>
                    </div>
                </div>

                {/* Type Summary */}
                {transTypeSummary.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 mb-5">
                        {transTypeSummary.map((s) => {
                            const net = s.total_debit - s.total_credit;
                            return (
                                <div key={s.trans_type} className="bg-white rounded-xl border border-gray-100 shadow-sm px-3 py-2.5">
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-[10px] font-bold uppercase text-gray-500 tracking-wide">{getTransactionTypeLabel(s.trans_type)}</span>
                                        <span className="text-[10px] font-bold bg-[#EDE4DA] text-[#2a2e30] rounded-full px-1.5 py-0.5">{s.count}</span>
                                    </div>
                                    {s.total_debit > 0 && <div className="text-[11px] text-red-600 font-semibold text-right">{formatMoney(s.total_debit)}</div>}
                                    {s.total_credit > 0 && <div className="text-[11px] text-green-600 font-semibold text-right">{formatMoney(s.total_credit)}</div>}
                                    <div className={`text-xs font-bold text-right border-t border-gray-100 mt-1.5 pt-1.5 ${net >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        {formatMoney(net)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Main Card */}
                <Card className="bg-white rounded-2xl shadow-xl">
                    <CardBody className="p-4">

                        {/* Filters */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">From</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none"
                                    value={filters.date_from}
                                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">To</label>
                                <input
                                    type="date"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none"
                                    value={filters.date_to}
                                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Type</label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none bg-white"
                                    value={filters.trans_type}
                                    onChange={(e) => handleFilterChange('trans_type', e.target.value)}
                                >
                                    <option value="">All Types</option>
                                    <option value="REM">Remittance</option>
                                    <option value="PAY">Payment</option>
                                    <option value="RIM">Reimbursement</option>
                                    <option value="CHG">Charge</option>
                                    <option value="ADJ">Adjustment</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Car</label>
                                <select
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF5733] focus:border-transparent outline-none bg-white"
                                    value={filters.plate_no}
                                    onChange={(e) => handleFilterChange('plate_no', e.target.value)}
                                >
                                    <option value="">All Cars</option>
                                    {cars.map(car => <option key={car.plate_no} value={car.plate_no}>{car.plate_no}</option>)}
                                </select>
                            </div>
                            <div className="flex items-end gap-2">
                                <Button onClick={fetchStatement} className="flex-1 bg-[#FF5733] hover:bg-[#e64520] text-white rounded-sm px-3 h-[38px] shadow-none text-sm font-semibold normal-case">
                                    Filter
                                </Button>
                                <Button onClick={clearFilters} className="bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-sm px-3 h-[38px] shadow-none text-sm normal-case">
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {/* Table */}
                        {loading ? (
                            <div className="py-10 text-center text-sm text-gray-400">Loading...</div>
                        ) : (
                            <div className="overflow-x-auto rounded-xl border border-gray-100">
                                <table className="w-full min-w-[900px] table-fixed text-left">
                                    <thead className="bg-slate-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[90px]">ID</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[95px]">Date</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[70px]">Type</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[160px]">Reference</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[80px]">Car</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase text-right w-[90px]">Debit</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase text-right w-[90px]">Credit</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase text-right w-[90px]">Gross</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase text-right w-[90px]">Discount</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase text-right w-[100px]">Balance</th>
                                            <th className="px-2 py-2.5 font-bold text-xs text-gray-500 uppercase w-[72px]">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {statement.length === 0 ? (
                                            <tr><td colSpan="11" className="px-3 py-8 text-center text-sm text-gray-400">No transactions found</td></tr>
                                        ) : (
                                            statement.map((trans) => {
                                                const isRem = trans.trans_type === 'REM';
                                                const detailLines = Array.isArray(trans.detail_lines) ? trans.detail_lines : [];
                                                const isExpanded = expandedRows.has(trans.id);
                                                const carCount = detailLines.length;
                                                const transPath = buildOwnerTransactionPath(trans.owner_code, trans.id);

                                                return (
                                                    <React.Fragment key={trans.id}>
                                                        <tr className={`border-b border-gray-50 transition-colors ${isReversedDocument(trans) ? 'bg-red-50/60 hover:bg-red-50' : 'hover:bg-slate-50'}`}>
                                                            <td className="px-2 py-1.5 align-middle">
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    {transPath ? (
                                                                        <Link to={transPath} className="text-xs font-mono font-semibold text-[#FF5733] hover:underline">{getDocumentNumber(trans.id)}</Link>
                                                                    ) : (
                                                                        <span className="text-xs font-mono text-gray-600">{getDocumentNumber(trans.id)}</span>
                                                                    )}
                                                                    {isReversedDocument(trans) && (
                                                                        <span className="px-1 py-0.5 bg-red-100 text-red-600 rounded text-[9px] font-bold uppercase">Rev</span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="px-2 py-1.5 align-middle text-xs text-gray-600 whitespace-nowrap">{formatDate(trans.date)}</td>
                                                            <td className="px-2 py-1.5 align-middle">
                                                                <Tooltip content={getTransactionTypeLabel(trans.trans_type)} placement="top">
                                                                    <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase cursor-help">{trans.trans_type}</span>
                                                                </Tooltip>
                                                            </td>
                                                            <td className="px-2 py-1.5 align-middle">
                                                                {trans.reference_type && trans.reference_id ? (
                                                                    <Tooltip content={trans.description ? `${trans.reference_type}: ${trans.reference_id} — ${trans.description}` : `${trans.reference_type}: ${trans.reference_id}`} placement="top">
                                                                        <span className="text-xs text-gray-600 truncate block max-w-[155px] cursor-help">{trans.reference_type}: {trans.reference_id}</span>
                                                                    </Tooltip>
                                                                ) : <span className="text-gray-300">—</span>}
                                                            </td>
                                                            <td className="px-2 py-1.5 align-middle">
                                                                {isRem && carCount > 0 ? (
                                                                    <button type="button" onClick={() => toggleRowExpand(trans.id)} className="flex items-center gap-1 text-xs font-semibold text-[#FF5733] hover:text-[#e64520]">
                                                                        {isExpanded ? <FaChevronDown className="text-[9px]" /> : <FaChevronRight className="text-[9px]" />}
                                                                        {carCount}c
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-xs text-gray-600">{trans.car_plate_no || <span className="text-gray-300">—</span>}</span>
                                                                )}
                                                            </td>
                                                            <td className="px-2 py-1.5 align-middle text-xs text-right font-semibold text-red-600">{trans.debit > 0 ? fmtAmt(trans.debit) : <span className="text-gray-300">—</span>}</td>
                                                            <td className="px-2 py-1.5 align-middle text-xs text-right font-semibold text-green-600">{trans.credit > 0 ? fmtAmt(trans.credit) : <span className="text-gray-300">—</span>}</td>
                                                            <td className="px-2 py-1.5 align-middle text-xs text-right text-gray-600">{trans.gross_amount > 0 ? fmtAmt(trans.gross_amount) : <span className="text-gray-300">—</span>}</td>
                                                            <td className="px-2 py-1.5 align-middle text-xs text-right text-gray-600">{trans.discount_amount > 0 ? fmtAmt(trans.discount_amount) : <span className="text-gray-300">—</span>}</td>
                                                            <td className={`px-2 py-1.5 align-middle text-xs text-right font-bold ${trans.running_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmtAmt(trans.running_balance)}</td>
                                                            <td className="px-2 py-1.5 align-middle">
                                                                <div className="flex items-center gap-1">
                                                                    <Tooltip content="Open transaction" placement="top">
                                                                        <Link to={`/owners/transactions/${encodeURIComponent(trans.owner_code)}/${encodeURIComponent(trans.id)}`} className="h-6 w-6 flex items-center justify-center rounded bg-slate-100 text-slate-600 hover:bg-slate-200 transition">
                                                                            <FaEye className="w-3 h-3" />
                                                                        </Link>
                                                                    </Tooltip>
                                                                    {canReverse(trans) && (
                                                                        <Tooltip content="Reverse" placement="top">
                                                                            <button type="button" onClick={() => openReverse(trans)} className="h-6 w-6 flex items-center justify-center rounded bg-red-50 text-red-600 hover:bg-red-100 transition">
                                                                                <FaUndoAlt className="w-3 h-3" />
                                                                            </button>
                                                                        </Tooltip>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                        {isRem && isExpanded && detailLines.map((detail, idx) => (
                                                            <tr key={`${trans.id}-d-${idx}`} className="bg-orange-50/40 border-b border-orange-100">
                                                                <td colSpan="3" className="px-2 py-1 pl-8">
                                                                    <span className="text-[10px] text-gray-400 italic">car detail</span>
                                                                </td>
                                                                <td className="px-2 py-1"></td>
                                                                <td className="px-2 py-1 text-xs font-semibold text-[#2a2e30]">{detail.plate_no || '-'}</td>
                                                                <td className="px-2 py-1"></td>
                                                                <td className="px-2 py-1 text-xs text-right font-semibold text-green-700">{detail.base_amount > 0 ? fmtAmt(detail.base_amount) : '-'}</td>
                                                                <td colSpan="4" className="px-2 py-1"></td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>

            {showTransactionForm && (
                <OwnerTransactionForm
                    open={showTransactionForm}
                    onClose={() => setShowTransactionForm(false)}
                    onSuccess={() => { setShowTransactionForm(false); fetchStatement(); }}
                    ownerCode={ownerCode}
                    cars={cars}
                />
            )}

            {reverseOpen && (
                <ReverseTransactionDialog
                    open={reverseOpen}
                    onClose={closeReverse}
                    ownerCode={ownerCode}
                    transaction={selectedTrans}
                    onSuccess={() => { closeReverse(); fetchStatement(); }}
                />
            )}

        </div>
    );
};

export default OwnerStatement;

